
import Vue from 'vue';
import '@/assets/css/helpdesk/helpdesk-theme.css';
import Component from 'vue-class-component';
import HelpdeskAssignee from '@/components/helpdesk/HelpdeskAssignee.vue';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import {settings} from '@/plugins/settings';
import {TICKET_STATUSES, TICKET_STATUS_COLORS, TicketStatus, ticketStatusLabel, ticketStatusShortLabel} from '@/helpers/helpdeskTicketStatus';

interface ITicketStats {
  open: number;
  pending: number;
  resolved: number;
  closed: number;
}

interface IRecentTicket {
  id: number;
  subject: string;
  status: string;
  reporter_email: string | null;
  assignee_email: string | null;
  created_at: string;
}

interface ITicketDay {
  day: string;
  count: number;
  // Tickets created that week, by their current status. Missing on older helpdesk deploys.
  by_status?: {[status in TicketStatus]: number};
}

interface IWeekSegment {
  status: string;
  label: string;
  value: number;
  color: string;
}

interface ITicketByType {
  name: string;
  count: number;
}

interface IDashboardData {
  pending_emails_count: number;
  recent_tickets: IRecentTicket[];
  ticket_stats: ITicketStats;
  tickets_by_day: ITicketDay[];
  tickets_by_type: ITicketByType[];
  avg_resolution_hours: number | null;
}

@Component({name: 'HelpdeskDashboard', components: {HelpdeskAssignee}})
export default class HelpdeskDashboard extends Vue {
  loading = false;
  // Index of the week whose tooltip is shown (hover or keyboard focus).
  hoverWeek: number | null = null;
  lastUpdated: Date | null = null;
  private clock = Date.now();
  private clockTimer: any = null;
  stats: IDashboardData = {
    pending_emails_count: 0,
    recent_tickets: [],
    ticket_stats: {open: 0, pending: 0, resolved: 0, closed: 0},
    tickets_by_day: [],
    tickets_by_type: [],
    avg_resolution_hours: null,
  };

  get currentUserEmail(): string {
    const u = this.$store.getters['authModule/userData'];
    return (u && u.email) ? u.email : '';
  }

  get totalTickets(): number {
    const s = this.stats.ticket_stats;
    return s.open + s.pending + s.resolved + s.closed;
  }

  get donutSlices() {
    const total = this.totalTickets;
    if (total === 0) return [];
    const entries = [
      {label: this.statusShortLabel('open'),     value: this.stats.ticket_stats.open,     color: TICKET_STATUS_COLORS.open},
      {label: this.statusShortLabel('pending'),  value: this.stats.ticket_stats.pending,   color: TICKET_STATUS_COLORS.pending},
      {label: this.statusShortLabel('resolved'), value: this.stats.ticket_stats.resolved,  color: TICKET_STATUS_COLORS.resolved},
      {label: this.statusShortLabel('closed'),   value: this.stats.ticket_stats.closed,    color: TICKET_STATUS_COLORS.closed},
    ];
    let cumulative = 0;
    return entries.map(e => {
      const pct = (e.value / total) * 100;
      const dashoffset = -cumulative;
      cumulative += pct;
      return {
        label: e.label,
        color: e.color,
        dasharray: pct + ' ' + (100 - pct),
        dashoffset,
      };
    });
  }

  get avgResolutionDisplay(): string {
    const h = this.stats.avg_resolution_hours;
    if (h === null || h === undefined) return '—';
    if (h < 1) return '<1h';
    if (h < 24) return Math.round(h) + 'h';
    return (h / 24).toFixed(1) + 'd';
  }

  get maxTypeCount(): number {
    const types = this.stats.tickets_by_type;
    if (!types || types.length === 0) return 1;
    return Math.max(...types.map((t: ITicketByType) => t.count));
  }

  typeBarPct(count: number): number {
    const max = this.maxTypeCount;
    return max > 0 ? Math.round((count / max) * 100) : 0;
  }

  get weekLegend(): Array<{status: string; label: string; title: string; color: string}> {
    return TICKET_STATUSES.map((status) => ({
      status,
      label: this.statusShortLabel(status),
      title: this.statusLabel(status),
      color: TICKET_STATUS_COLORS[status],
    }));
  }

  get dayBarsMax(): number {
    const data = this.stats.tickets_by_day;
    if (!data || data.length === 0) return 0;
    return Math.max(...data.map((d: ITicketDay) => d.count));
  }

  // Clean tick step (1, 2, 5, 10, 20, 50…) giving at most ~4 gridlines.
  get dayTickStep(): number {
    const max = this.dayBarsMax;
    let step = 1;
    for (const base of [1, 2, 5]) {
      for (let mag = 1; mag <= 1000; mag *= 10) {
        step = base * mag;
        if (max / step <= 4) return step;
      }
    }
    return step;
  }

  // Top of the scale: the first tick at or above the tallest bar, so the ticks line up with the bars.
  get dayScaleMax(): number {
    const step = this.dayTickStep;
    return Math.max(step, Math.ceil(this.dayBarsMax / step) * step);
  }

  get dayTicks(): Array<{value: number; pct: number}> {
    const ticks: Array<{value: number; pct: number}> = [];
    for (let v = this.dayTickStep; v <= this.dayScaleMax; v += this.dayTickStep) {
      ticks.push({value: v, pct: (v / this.dayScaleMax) * 100});
    }
    return ticks;
  }

  get dayBarsData() {
    const data = this.stats.tickets_by_day;
    if (!data || data.length === 0) return [];
    const scale = this.dayScaleMax;
    const n = data.length;
    const peak = this.dayBarsMax;
    let peakLabelled = false;
    return data.map((d: ITicketDay, i: number) => {
      const parts = d.day ? d.day.split('-') : ['0', '0', '0'];
      const label = parseInt(parts[2], 10) + '/' + parseInt(parts[1], 10);
      const isCurrent = i === n - 1;
      const breakdown: IWeekSegment[] = d.by_status
        ? TICKET_STATUSES.map((status) => ({
            status,
            label: this.statusLabel(status),
            value: d.by_status ? d.by_status[status] || 0 : 0,
            color: TICKET_STATUS_COLORS[status],
          }))
        // Older backend without the breakdown: one neutral block for the total.
        : [{status: 'all', label: this.$t('helpdesk.dashboard.weekCreated') as string, value: d.count, color: '#8590a2'}];
      // Cap labels only on the current week and the (first) busiest one; the tooltip has the rest.
      const isPeak = d.count === peak && d.count > 0 && !peakLabelled;
      if (isPeak) peakLabelled = true;
      const aria = this.$t('helpdesk.dashboard.weekOf', {date: label}) + ': ' + d.count + ' ' + this.$t('helpdesk.dashboard.weekCreated')
        + (d.by_status ? ' (' + breakdown.map(seg => seg.value + ' ' + seg.label).join(', ') + ')' : '');
      return {
        day: d.day,
        label,
        count: d.count,
        pct: d.count > 0 ? (d.count / scale) * 100 : 0,
        isCurrent,
        showCount: d.count > 0 && (isCurrent || isPeak),
        segments: breakdown.filter(seg => seg.value > 0),
        breakdown,
        aria,
      };
    });
  }

  // Last bucket = current week (in progress); the one before is the last complete week.
  get weekCounts(): {current: number; previous: number} {
    const d = this.stats.tickets_by_day || [];
    return {current: d.length ? d[d.length - 1].count : 0, previous: d.length > 1 ? d[d.length - 2].count : 0};
  }

  get weeklyAverage(): string {
    const d = this.stats.tickets_by_day || [];
    if (!d.length) return '0';
    const avg = d.reduce((sum, w) => sum + w.count, 0) / d.length;
    return avg < 10 ? avg.toFixed(1).replace('.', ',') : String(Math.round(avg));
  }

  get kpis(): Array<{key: string; label: string; value: string | number; icon: string; tone: string; to?: any; hint: string;
                     note?: string; trend?: {dir: string; text: string}}> {
    const s = this.stats.ticket_stats;
    const w = this.weekCounts;
    const diff = w.current - w.previous;
    return [
      {key: 'emails',   label: 'Emails sin leer',      value: this.stats.pending_emails_count, icon: 'mdi-email-alert-outline', tone: 'warning',
       to: {name: 'helpdeskInbox'}, hint: 'Emails en estado pendiente en el Inbox', note: 'en el Inbox'},
      {key: 'open',     label: this.$t('helpdesk.dashboard.kpiOpen') as string, value: s.open, icon: 'mdi-ticket-outline', tone: 'info',
       to: {name: 'helpdeskTickets'}, hint: this.$t('helpdesk.dashboard.kpiOpenHint') as string, note: 'últimos 3 meses'},
      {key: 'pending',  label: this.$t('helpdesk.dashboard.kpiPending') as string, value: s.pending, icon: 'mdi-timer-sand', tone: 'warning',
       to: {name: 'helpdeskTickets'}, hint: this.$t('helpdesk.dashboard.kpiPendingHint') as string, note: this.$t('helpdesk.dashboard.kpiPendingNote') as string},
      {key: 'week',     label: 'Creados esta semana',  value: w.current, icon: 'mdi-calendar-week',         tone: 'neutral',
       to: {name: 'helpdeskTickets'}, hint: 'Desde el lunes, comparado con la semana anterior completa',
       trend: {dir: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat', text: (diff > 0 ? '+' : '') + diff + ' vs semana anterior'}},
      {key: 'done',     label: this.$t('helpdesk.dashboard.kpiDone') as string, value: s.resolved + s.closed, icon: 'mdi-check-circle-outline', tone: 'success',
       hint: this.$t('helpdesk.dashboard.kpiDoneHint') as string, note: 'últimos 3 meses'},
      {key: 'time',     label: 'Tiempo medio resolución', value: this.avgResolutionDisplay, icon: 'mdi-clock-outline', tone: 'neutral',
       hint: 'Aproximado: de la creación a la última modificación de los tickets resueltos/cerrados', note: 'aproximado'},
    ];
  }

  get lastUpdatedLabel(): string {
    if (!this.lastUpdated) return '—';
    const mins = Math.floor((this.clock - this.lastUpdated.getTime()) / 60000);
    if (mins < 1) return 'ahora mismo';
    if (mins === 1) return 'hace 1 min';
    if (mins < 60) return 'hace ' + mins + ' min';
    return 'a las ' + this.lastUpdated.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }

  relativeDate(dateStr: string): string {
    if (!dateStr) return '';
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 60) return 'hace ' + Math.max(mins, 1) + ' min';
    const hours = Math.floor(mins / 60);
    if (hours < 24) return 'hace ' + hours + ' h';
    const days = Math.floor(hours / 24);
    if (days < 7) return 'hace ' + days + (days === 1 ? ' día' : ' días');
    return this.shortDate(dateStr);
  }

  get donutLegend() {
    const s = this.stats.ticket_stats;
    return [
      {label: this.statusShortLabel('open'),     title: this.statusLabel('open'),     value: s.open,     color: TICKET_STATUS_COLORS.open},
      {label: this.statusShortLabel('pending'),  title: this.statusLabel('pending'),  value: s.pending,   color: TICKET_STATUS_COLORS.pending},
      {label: this.statusShortLabel('resolved'), title: this.statusLabel('resolved'), value: s.resolved,  color: TICKET_STATUS_COLORS.resolved},
      {label: this.statusShortLabel('closed'),   title: this.statusLabel('closed'),   value: s.closed,    color: TICKET_STATUS_COLORS.closed},
    ];
  }

  created() {
    this.loadStats();
    // Keeps "Actualizado hace X" honest without refetching.
    this.clockTimer = setInterval(() => { this.clock = Date.now(); }, 30000);
  }

  beforeDestroy() {
    clearInterval(this.clockTimer);
  }

  loadStats() {
    this.loading = true;
    helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/dashboard', method: 'GET'})
      .then((res: any) => {
        const d = (res.data && res.data.data) ? res.data.data : null;
        if (d) this.stats = d;
        this.lastUpdated = new Date();
        this.clock = Date.now();
      })
      .finally(() => { this.loading = false; });
  }


  statusLabel(status: string): string {
    return ticketStatusLabel(this.$i18n, status);
  }

  statusShortLabel(status: string): string {
    return ticketStatusShortLabel(this.$i18n, status);
  }

  openTicket(id: number) {
    this.$router.push({name: 'helpdeskTickets', query: {open_ticket: String(id)}});
  }

  shortDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'});
  }
}
